import React, { useEffect, useState } from 'react';
import { Alert, Button, Dropdown, message, Spin, MenuProps, Modal } from 'antd';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  CarOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
  PrinterOutlined,
  ReloadOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { FaList } from 'react-icons/fa';
import BackButton from '@app/components/common/BackButton/BackButton';
import { useAppDispatch, useAppSelector } from '@app/hooks/reduxHooks';
import {
  deleteOrder,
  getOrder,
  printCombineOrder,
  sendZaloMessage,
  updateStatusOrder,
  getOrders,
} from '@app/store/slices/orderSlice';
import OrderDetailTabs from './components/OrderDetailTabs';
import ReactDOMServer from 'react-dom/server';
import PrintOrder from './detail/PrintOrder';
import { printHtmlBody } from '@app/utils/printDocument';
import { orderQrUrl as buildOrderQrUrl, toQrDataUrl } from '@app/utils/orderQr';
import { SiZalo } from 'react-icons/si';
import { TbTruckReturn } from 'react-icons/tb';
import { BankLookupItem, getBankLookupApi, getBankShopQRCode } from '@app/api/bank.api';
import type { OrderModel } from '@app/interfaces/order';
import { getOnePayQRBase64 } from '@app/api/onepay.api';
import { hasOutstandingDebt, outstandingAmount } from '@app/utils/orderDebt';
import OnePayQRModal from './OnePay/OnePayQRModal';
import { MdOutlineTransferWithinAStation } from 'react-icons/md';
import OrderTransferCustomerForm from './OrderTransferCustomerForm';
import { VscCombine } from 'react-icons/vsc';
import CombineOrderForm from './CombineOrderForm';
import CancelOrderForm from './CancelOrderForm';
import CompleteOrderForm from './CompleteOrderForm';
import { useAppSetting } from '@app/contexts/AppSettingContext';
import { useBreadcrumbLabel } from '@app/components/layouts/main/MainLayout/BreadcrumbContext';

const OrderDetail = () => {
  const { logoUrl, storeName, storeAddress, storePhone, storeWebsite, onepayEnable } = useAppSetting();
  const { id: orderId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showTransferCustomerForm, setShowTransferCustomerForm] = useState(false);
  const [showCombineOrderForm, setShowCombineOrderForm] = useState(false);
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [showCompleteForm, setShowCompleteForm] = useState(false);
  const [showOnePayModal, setShowOnePayModal] = useState(false);
  const [activitiesRefreshToken, setActivitiesRefreshToken] = useState(0);
  const [bank, setBank] = useState<BankLookupItem | undefined>(undefined);

  const { record, pagination, filters, sorter, searchTerm } = useAppSelector((state) => state.order);

  // Breadcrumb hiển thị mã nghiệp vụ thay cho "Chi tiết #id".
  useBreadcrumbLabel(String(record?.id) === orderId ? record?.order_code : undefined);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    allow_create,
    allow_update,
    allow_cancel,
    allow_delete,
    allow_send_zalo_message,
    allow_create_return_order,
    allow_transfer_order,
    allow_complete,
    allow_change_status,
    allow_combine_order,
    allow_print_combine,
    allow_onepay_payment,
  } = record?.permission || {};

  useEffect(() => {
    if (orderId) {
      fetchOrder();
    }

    fetchBanks();
  }, [orderId, dispatch]);

  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    if (!paymentStatus) return;

    if (paymentStatus === 'success') {
      message.success('Thanh toán OnePay thành công! Đơn hàng đã được cập nhật.');
      if (orderId) dispatch(getOrder(Number(orderId)));
    } else if (paymentStatus === 'failed') {
      message.error('Thanh toán OnePay thất bại hoặc bị huỷ.');
    }

    // Remove ?payment= from URL without navigating
    setSearchParams(
      (prev) => {
        prev.delete('payment');
        return prev;
      },
      { replace: true },
    );
  }, [searchParams]);

  const fetchOrder = () => {
    dispatch(getOrder(Number(orderId)));
  };

  // Dùng /banks/lookup (mọi user đã đăng nhập) thay vì /banks (cần quyền banks:index):
  // user chỉ được xem đơn vẫn mở được chi tiết mà không bị 403 đẩy sang /403.
  const fetchBanks = async () => {
    const rows = await getBankLookupApi();
    const shopBank = rows.find((item) => item.primary) ?? rows[0];
    setBank(shopBank);

    // QR thanh toán được tạo ngay lúc in trong resolveQRCode() theo số CÒN PHẢI THU;
    // không dựng sẵn ở đây để khỏi giữ một mã theo tổng đơn đã lỗi thời.
  };

  const handleCreateOrder = () => {
    navigate('/orders/new');
  };

  const handleBackToListOrder = () => {
    navigate('/orders/list');
  };

  const handleReload = () => {
    try {
      fetchOrder();
      message.success('Reload successful!');
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const handleUpdateStatus = (value: number) => {
    dispatch(
      updateStatusOrder({
        orderId: Number(orderId),
        formData: {
          status_id: value,
        },
      }),
    ).then(() => {
      // Refetch orders list to reflect status change in the list view
      const params: any = {
        page: pagination.current,
        per_page: pagination.pageSize,
        ...filters,
        order_type: 'order',
        order_code_or_customer_name_cont: searchTerm,
      };
      if (sorter.field) {
        params.sort = sorter.field;
        params.order = sorter.order === 'ascend' ? 'asc' : 'desc';
      }
      dispatch(getOrders(params));
    });
  };

  const handleCreateReturnOrder = () => {
    navigate(`/order-returns/new?id=${record?.id}`);
  };

  const handleCombineOrder = () => {
    setShowCombineOrderForm(true);
  };

  const handleCancelOrder = () => {
    setShowCancelForm(true);
  };

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    const key = e.key;

    switch (key) {
      case '3':
      case '5':
      case '6':
        handleUpdateStatus(Number(key));
        break;
      case '7':
        setShowCompleteForm(true);
        break;
      case '8':
        handleCancelOrder();
        // Modal.confirm({
        //   title: "Xác nhận!",
        //   content: "Bạn có chắc chuyển trạng thái huỷ đơn?",
        //   onOk() {
        //     handleUpdateStatus(Number(key));
        //   }
        // })

        break;
      case '9':
        break;
      case '10':
        handleCreateReturnOrder();
        break;
      case '11':
        handleCreateOrder();
        break;
      case '12':
        handleEditOrder();
        break;
      case '13':
        Modal.confirm({
          title: 'Xác nhận!',
          content: 'Bạn có chắc xoá đơn hàng?',
          onOk() {
            try {
              dispatch(deleteOrder(Number(orderId)));
              navigate('/orders/list');
            } catch (error: any) {
              message.error(error.message);
            }
          },
        });
        break;
      case '14':
        Modal.confirm({
          title: 'Xác nhận!',
          content: 'Bạn có chắc gửi tin nhắn zalo xác nhận cho khách hàng?',
          onOk() {
            try {
              dispatch(sendZaloMessage({ orderId: Number(orderId) }));
            } catch (error: any) {
              message.error(error.message);
            }
          },
        });
        break;
      case '15':
        handleTransferCustomer();
        break;
      case '16':
        handleCombineOrder();
        break;
    }
  };

  const items: MenuProps['items'] = [
    // ── Nhóm 1: Trạng thái đơn hàng ─────────────────────────────
    ...(allow_change_status || allow_complete || allow_cancel
      ? [
          {
            type: 'group' as const,
            label: 'Trạng thái đơn hàng',
            children: [
              ...(allow_change_status
                ? [
                    { label: 'Đang xử lý', key: '3', icon: <SyncOutlined className="text-primary" /> },
                    { label: 'Chuyển giao hàng', key: '5', icon: <CarOutlined className="text-primary" /> },
                    { label: 'Đang giao hàng', key: '6', icon: <CarOutlined className="text-purple" /> },
                  ]
                : []),
              ...(allow_complete
                ? [
                    {
                      label: <strong className="text-success">Hoàn thành</strong>,
                      key: '7',
                      icon: <CheckCircleOutlined className="text-success" />,
                    },
                  ]
                : []),
              ...(allow_cancel ? [{ label: 'Huỷ đơn', key: '8', icon: <MinusCircleOutlined />, danger: true }] : []),
            ],
          },
        ]
      : []),

    // ── Nhóm 2: Quản lý đơn hàng ─────────────────────────────────
    ...(allow_update || allow_create || allow_create_return_order
      ? [
          { type: 'divider' as const },
          {
            type: 'group' as const,
            label: 'Quản lý đơn hàng',
            children: [
              ...(allow_update
                ? [{ label: 'Cập nhật đơn hàng', key: '12', icon: <EditOutlined className="text-primary" /> }]
                : []),
              ...(allow_create
                ? [{ label: 'Tạo mới đơn hàng', key: '11', icon: <PlusCircleOutlined className="text-primary" /> }]
                : []),
              ...(allow_create_return_order
                ? [{ label: 'Tạo phiếu trả hàng', key: '10', icon: <TbTruckReturn className="text-warning" /> }]
                : []),
            ],
          },
        ]
      : []),

    // ── Nhóm 3: Thao tác khác ─────────────────────────────────────
    ...(allow_send_zalo_message || allow_transfer_order || allow_combine_order
      ? [
          { type: 'divider' as const },
          {
            type: 'group' as const,
            label: 'Thao tác khác',
            children: [
              ...(allow_send_zalo_message
                ? [{ label: 'Gửi tin nhắn Zalo', key: '14', icon: <SiZalo className="text-primary" /> }]
                : []),
              ...(allow_transfer_order
                ? [
                    {
                      label: 'Chuyển đơn cho khách hàng',
                      key: '15',
                      icon: <MdOutlineTransferWithinAStation className="text-warning" />,
                    },
                  ]
                : []),
              ...(allow_combine_order
                ? [{ label: 'Gộp đơn hàng', key: '16', icon: <VscCombine className="text-warning" /> }]
                : []),
            ],
          },
        ]
      : []),

    // ── Xoá đơn — cuối cùng, màu đỏ ──────────────────────────────
    ...(allow_delete
      ? [{ type: 'divider' as const }, { label: 'Xoá đơn hàng', key: '13', icon: <DeleteOutlined />, danger: true }]
      : []),
  ].filter(Boolean);

  const menuProps = {
    items,
    onClick: handleMenuClick,
  };

  const absoluteLogoUrl =
    logoUrl.startsWith('http') || logoUrl.startsWith('data:')
      ? logoUrl
      : `${window.location.origin}${logoUrl.startsWith('/') ? '' : '/'}${logoUrl}`;

  const fetchImageAsBase64 = async (url: string): Promise<string> => {
    if (!url || url.startsWith('data:')) return url;
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch {
      return url;
    }
  };

  const printContent = (
    record: any,
    qrCodeUrl: string,
    qrType: 'vietqr' | 'onepay',
    logoSrc = absoluteLogoUrl,
    orderQrDataUrl?: string,
  ) => {
    return ReactDOMServer.renderToString(
      <PrintOrder
        record={record}
        qrCodeUrl={qrCodeUrl}
        qrType={qrType}
        logoUrl={logoSrc}
        storeName={storeName}
        storeAddress={storeAddress}
        storePhone={storePhone}
        storeWebsite={storeWebsite}
        orderQrUrl={orderQrDataUrl}
      />,
    );
  };

  // QR mở trang xử lý nhanh của đơn — shipper quét trên phiếu để cập nhật giao hàng.
  // Đơn gộp có nhiều mã: lấy mã đầu tiên. Lỗi sinh QR không được chặn việc in.
  const buildOrderQr = async (target: OrderModel): Promise<string | undefined> => {
    const code = Array.isArray(target.order_code) ? target.order_code[0] : target.order_code;
    if (!code) return undefined;

    try {
      return await toQrDataUrl(buildOrderQrUrl(code), 300);
    } catch {
      return undefined;
    }
  };

  const openPrintWindow = async (printContent: string) => {
    try {
      await printHtmlBody(printContent, {
        title: 'In đơn hàng',
        headHtml: `
          <style>
            .logo-company {
              width: 150px !important;
              height: auto !important;
              display: block;
              align-self: flex-start;
              margin-top: -8px;
              border: none;
              outline: none;
            }
          </style>
        `,
      });
    } catch (error) {
      message.error('An error occurred while printing.');
    }
  };

  const resolveQRCode = async (targetRecord: OrderModel): Promise<{ url: string; type: 'vietqr' | 'onepay' }> => {
    // Đơn đã thu đủ (debit_amount = 0) hoặc đã thu thừa (debit_amount > 0) thì OnePay
    // không tạo được QR — chỉ đơn CÒN NỢ (debit_amount < 0) mới có tiền để thu.
    // (không còn số tiền cần thu) — bỏ qua, dùng thẳng VietQR.
    if (onepayEnable && targetRecord.id && hasOutstandingDebt(targetRecord.debit_amount)) {
      const qr = await getOnePayQRBase64('Order', Number(targetRecord.id));
      if (qr) return { url: qr, type: 'onepay' };
      message.warning('Không thể tải QR OnePay, đang dùng VietQR thay thế.');
    }
    // QR chuyển khoản cũng phải theo số CÒN PHẢI THU, không phải tổng giá trị đơn:
    // đơn đã thu một phần mà in total_amount thì khách chuyển thừa đúng phần đã trả.
    // Không còn gì để thu (thu đủ / thu thừa) thì không in mã thanh toán nào cả.
    const amount = outstandingAmount(targetRecord.debit_amount);
    if (amount <= 0) return { url: '', type: 'vietqr' };

    const description = `Chuyen khoan`;
    return { url: (await getBankShopQRCode(bank, amount, description)) ?? '', type: 'vietqr' };
  };

  const handlePrintOrder = async () => {
    try {
      // Refetch trước khi in: giấy in và QR OnePay (backend tính từ DB) phải cùng
      // một số tiền — record trong Redux có thể cũ nếu đơn vừa được sửa nơi khác.
      const freshRecord = (await dispatch(getOrder(Number(orderId))).unwrap()) as OrderModel;
      const [qrResult, logoBase64, orderQr] = await Promise.all([
        resolveQRCode(freshRecord),
        fetchImageAsBase64(absoluteLogoUrl),
        buildOrderQr(freshRecord),
      ]);
      openPrintWindow(printContent(freshRecord, qrResult.url, qrResult.type, logoBase64, orderQr));
    } catch (error) {
      message.error('An error occurred while printing.');
    }
  };

  const handlePrintCombineOrder = async () => {
    try {
      const response = await dispatch(printCombineOrder({ orderId: Number(record?.id) }));
      const newRecord = response.payload as OrderModel | undefined;

      if (newRecord) {
        const [qrResult, logoBase64, orderQr] = await Promise.all([
          resolveQRCode(newRecord),
          fetchImageAsBase64(absoluteLogoUrl),
          buildOrderQr(newRecord),
        ]);
        openPrintWindow(printContent(newRecord, qrResult.url, qrResult.type, logoBase64, orderQr));
      } else {
        message.error('Error fetch data printing!');
      }
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const handleEditOrder = () => {
    navigate(`/orders/${orderId}/edit`);
  };

  const handleTransferCustomer = () => {
    setShowTransferCustomerForm(true);
  };

  if (!record) {
    return <Spin />;
  }

  return (
    <>
      <div className="mb-3 flex-between">
        <div className="tw-flex tw-items-center tw-gap-2">
          <BackButton fallbackTo="/orders/list" />
          <div>
            Đơn hàng: <span className="text-primary font-bold-weight">{record.order_code}</span>
          </div>
        </div>
        <span className="flex-end">
          <Button onClick={handleReload} className="me-3" type="primary">
            <ReloadOutlined />
          </Button>
          <Button type="primary" onClick={handlePrintOrder} className="me-3">
            <PrinterOutlined /> Print
          </Button>

          {/* Cờ allow_* do backend tính = trạng thái đơn + quyền của user (OrderPolicy). */}
          {record?.group_code && allow_print_combine && (
            <Button type="primary" onClick={handlePrintCombineOrder} className="me-3">
              <PrinterOutlined /> Bill gộp
            </Button>
          )}

          {onepayEnable && allow_onepay_payment && hasOutstandingDebt((record as OrderModel)?.debit_amount) && (
            <Button type="primary" className="me-3" onClick={() => setShowOnePayModal(true)}>
              💳 Thanh toán OnePay
            </Button>
          )}

          {items.length > 0 && (
            <Dropdown.Button
              menu={menuProps}
              placement="bottom"
              icon={<DownOutlined />}
              type="primary"
              className="me-3"
              trigger={['click']}
            >
              Action
            </Dropdown.Button>
          )}

          <Button onClick={handleBackToListOrder} type="primary" title="Danh sách đơn hàng">
            <FaList />
          </Button>
        </span>
      </div>

      <Alert
        message="Informational notes"
        description={
          <ul>
            <li>
              Trường hợp đơn hàng ở trạng thái <b>"Mới"</b> hệ thống sẽ ghi nhận vào kho, và tạo phiếu thu tự động nếu
              có.
            </li>
            <li>
              Khi chuyển trạng thái của đơn hàng <b>"đang xử lý"</b> hệ thống sẽ gửi tin nhắn Zalo OA xác nhận cho khách
              hàng.
            </li>
            <li>
              Chỉ ghi nhận doanh số cho shipper ở trạng thái <b>"chuyển giao hàng"</b>.
            </li>
            <li>
              Chỉ ghi nhận công nợ thực tế khách hàng khi đơn hàng ở trạng thái <b>"hoàn thành"</b>.
            </li>
            <li>
              Khi khách hàng thanh toán (phát sinh phiếu thu) hệ thống sẽ gửi{' '}
              <b>tin nhắn Zalo OA xác nhận thanh toán cho khách hàng</b>.
            </li>
            <li>
              Khi thực hiện hoàn tất đơn hàng (đồng nghĩa với việc đã giao hàng thành công) hệ thống sẽ gửi{' '}
              <b>tin nhắn Zalo OA xác nhận đơn hàng đã giao thành công cho khách hàng.</b>.
            </li>
            <li>
              Khi tạo phiếu trả hàng hệ thống sẽ tự động chuyển trạng thái đơn hàng về <b>"đang xử lý"</b> để đảm bảo
              quy trình trả hàng được thực hiện đầy đủ.
            </li>
          </ul>
        }
        type="info"
        banner
        closable
        className="text-primary my-3"
      />

      <OrderDetailTabs record={record} activitiesRefreshToken={activitiesRefreshToken} />

      {showTransferCustomerForm && (
        <OrderTransferCustomerForm
          open={showTransferCustomerForm}
          record={record}
          onClose={() => {
            setShowTransferCustomerForm(false);
          }}
        />
      )}

      {showCombineOrderForm && (
        <CombineOrderForm
          open={showCombineOrderForm}
          record={record}
          onClose={() => {
            setShowCombineOrderForm(false);
          }}
        />
      )}

      {showCancelForm && (
        <CancelOrderForm
          open={showCancelForm}
          record={record}
          onClose={() => {
            setShowCancelForm(false);
          }}
        />
      )}

      {showCompleteForm && (
        <CompleteOrderForm
          open={showCompleteForm}
          record={record}
          onClose={() => setShowCompleteForm(false)}
          onSuccess={() => {
            fetchOrder();
            setActivitiesRefreshToken((t) => t + 1);
          }}
        />
      )}

      {showOnePayModal && record && (
        <OnePayQRModal
          open={showOnePayModal}
          onCancel={() => setShowOnePayModal(false)}
          record={record as OrderModel}
        />
      )}
    </>
  );
};

export default OrderDetail;
